import type { FC } from "react";
import React from "react";

import type { ActionAction, Pages } from "~/core";
import {
  isClickAction,
  isExternalLink,
  isInternalLink,
} from "~/core/utils/action.utils";
import type { MenuItemType } from "~/features/headerV3/HeaderV3";
import { ArrowDown } from "~/icons";
import {
  Background,
  Dropdown,
  Flex,
  Image,
  Typography,
  useUiKitComponents,
} from "~/shared/ui-kit";
import { useDirectus } from "~/shared/utils/directus/useDirectus";

type NavbarItemProps = MenuItemType & {
  currentNavlink: string;
  onNavlinkClick: (url: string, id: string) => void;
  clickHeaderSub: (targetUrl: string) => void;
  onCallAction: (action: ActionAction) => void;
  getUrl: (page: Pages) => string;
};

type MenuSubItemType = ExtractArrayItemType<NonNullable<MenuItemType>["items"]>;

function isValidDropdownItem(
  item: MenuSubItemType,
): item is NonNullable<MenuSubItemType> {
  return item !== null && item !== undefined;
}

export const NavbarItem: FC<NavbarItemProps> = ({
  id,
  variant,
  items,
  action,
  translations,
  currentNavlink,
  onNavlinkClick,
  clickHeaderSub,
  getUrl,
  onCallAction,
}) => {
  const { getDirectusImage } = useDirectus();
  const { Link } = useUiKitComponents();
  const nestedAction = action?.action?.[0];
  let href: string | undefined;
  if (isExternalLink(nestedAction?.item)) {
    href = nestedAction.item.href ?? undefined;
  }
  if (isInternalLink(nestedAction?.item)) {
    href = nestedAction.item.page ? getUrl(nestedAction.item.page) : undefined;
  }

  const translation = translations?.[0];
  const isActive = currentNavlink === id;

  const onClickDropdownItem = (key: string | number): void => {
    const menuItem = items?.find(
      (item) => item?.menu_action_sub_items_id?.id === key,
    );
    const dropdownAction =
      menuItem?.menu_action_sub_items_id?.action?.action?.[0];
    let dropdownHref: string | undefined;

    if (isExternalLink(dropdownAction?.item)) {
      dropdownHref = dropdownAction.item.href ?? undefined;
    }
    if (isInternalLink(dropdownAction?.item)) {
      dropdownHref = dropdownAction.item.page
        ? getUrl(dropdownAction.item.page)
        : undefined;
    }
    if (dropdownHref) clickHeaderSub(dropdownHref);
    if (isClickAction(dropdownAction?.item)) {
      onCallAction(dropdownAction);
    }
  };

  return (
    <Flex
      align="center"
      className="hidden px-2.5 lg:block relative w-full md:w-auto mr-3"
    >
      {variant === "default" && (
        <Flex
          align="center"
          justify="space-between"
          className={`transition-all duration-200 ease-linear hover:stroke-[#9b5ff5] hover:text-[#9b5ff5] ${isActive && "text-[#9b5ff5] stroke-[#9b5ff5]"}`}
        >
          {items && (
            <Dropdown.Trigger
              onAction={(key) => onClickDropdownItem(key)}
              onOpenChange={() => {
                if (href) onNavlinkClick(href, id);
                if (nestedAction) {
                  onCallAction(nestedAction);
                }
              }}
              strategy="hover"
              offset={40}
              triggerElement={
                <Link href={href}>
                  <Flex align="center" gap={10}>
                    <Typography size="medium">{translation?.name}</Typography>
                    <ArrowDown />
                  </Flex>
                </Link>
              }
            >
              {items
                .filter((item) => isValidDropdownItem(item))
                .map((item) => {
                  const itemAction =
                    item.menu_action_sub_items_id?.action?.action?.[0];
                  let itemHref;
                  if (isExternalLink(itemAction?.item)) {
                    itemHref = itemAction.item.href ?? undefined;
                  }
                  if (isInternalLink(itemAction?.item)) {
                    itemHref = itemAction.item.page
                      ? getUrl(itemAction.item.page)
                      : undefined;
                  }
                  return (
                    <Dropdown.Item
                      key={item.menu_action_sub_items_id?.id}
                      href={itemHref}
                      textValue={
                        item.menu_action_sub_items_id?.translations?.[0]
                          ?.name ?? ""
                      }
                    >
                      <Flex
                        className="py-3 px-4 w-[322px]"
                        align="center"
                        gap={8}
                      >
                        {item?.menu_action_sub_items_id?.icon?.id && (
                          <Image
                            value={getDirectusImage(
                              item.menu_action_sub_items_id.icon.id,
                              {
                                format: "svg",
                                width: 24,
                              },
                            )}
                          />
                        )}
                        <Typography size="medium">
                          {
                            item.menu_action_sub_items_id?.translations?.[0]
                              ?.name
                          }
                        </Typography>
                      </Flex>
                    </Dropdown.Item>
                  );
                })}
            </Dropdown.Trigger>
          )}
        </Flex>
      )}

      {variant === "underline" && (
        <Background
          bgValue="linear-gradient(90deg,#e2c1f9 0%,#febd8e 33%,#fbffe4 66%,#b6d0f7 100%);"
          className="px-4 py-1 rounded flex self-center"
        >
          <Link
            href={href}
            onClick={() => {
              if (href) onNavlinkClick(href, id);
              if (nestedAction) {
                onCallAction(nestedAction);
              }
            }}
          >
            <Typography
              size="medium"
              weight="semibold"
              className="text-[#28282d]"
            >
              {translation?.name}
            </Typography>
          </Link>
        </Background>
      )}

      {variant === "emphasized" && (
        <Flex
          align="center"
          className="transition-all duration-200 ease-linear group"
        >
          <Background
            bgValue="/icons/vip-star-icon.svg"
            groupHoverBgValue="/icons/vip-star-icon-hover.svg"
            className="min-h-[24px] min-w-[24px] mx-1"
          />
          <Link
            href={href}
            onClick={() => {
              if (href) onNavlinkClick(href, id);
              if (nestedAction) {
                onCallAction(nestedAction);
              }
            }}
          >
            <Typography
              size="small"
              weight="semibold"
              className="text-[#F1C144] group-hover:text-[#eeb115]"
            >
              {translation?.name}
            </Typography>
          </Link>
          <Background
            bgValue="/icons/vip-star-icon.svg"
            groupHoverBgValue="/icons/vip-star-icon-hover.svg"
            className="min-h-[24px] min-w-[24px] mx-1"
          />
        </Flex>
      )}

      {variant === "highlighted" && (
        <Background
          className="rounded px-2 py-1"
          bgValue="linear-gradient(271.26deg,#631cc6 1.4%,#bd75f5 46.85%,#631cc6 87.6%);"
        >
          <Link
            href={href}
            onClick={() => {
              if (href) onNavlinkClick(href, id);
              if (nestedAction) {
                onCallAction(nestedAction);
              }
            }}
          >
            <Typography
              size="medium"
              weight="semibold"
              className="text-[#fff] text-center"
            >
              {translation?.name}
            </Typography>
          </Link>
        </Background>
      )}
    </Flex>
  );
};
